import * as React from 'react';
import { FloatingPortal, FloatingOverlay, FloatingFocusManager, useMergeRefs } from '@floating-ui/react';
import { DialogContext } from './Dialog.context';
import { DIALOG_STATIC_STYLES, useDialogDynamicSurfaceStyles, useDialogRootVariables } from './Dialog.styles';
import { DialogProps } from './Dialog.types';
import { useDialog } from './useDialog';
import { FixMeLater } from '../../utils';
import { useComponentDefaultProps } from '../../theming';
import { Loader } from '../Loader';
import { DialogBackdrop } from './DialogBackdrop/DialogBackdrop';
import { Paper } from '../Paper';

const defaultProps: Partial<DialogProps> = {
    cornerStyle: 'square',
};

export const Dialog: React.ForwardRefExoticComponent<DialogProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Dialog(props: DialogProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element | null {
        const {
            children,
            SurfaceProps,
            size = 'm',
            fullScreen = 'never',
            position = 'centered',
            lockScroll = true,
            returnFocus = true,
            keepMounted = false,
            cornerStyle = 'square',
            margin = 5,
            BackdropProps,
            ...other
        } = useComponentDefaultProps('Dialog', defaultProps, props);

        const dialogContext = useDialog({ ...other });

        const {
            context: floatingContext,
            getFloatingProps,
            refs,
            open,
            isMounted,
            transitionStatus,
            labelId,
            descriptionId,
            loading,
            withAnimation,
        } = dialogContext;

        const rootRef = React.useRef<HTMLDivElement | null>(null);

        const cssVariables = useDialogRootVariables(size, cornerStyle, margin);
        const dynamicSurfaceStyles = useDialogDynamicSurfaceStyles(fullScreen, position);

        const shouldMount = isMounted || keepMounted;
        const hidden = !open && !isMounted;

        const handleMergedSurfaceRef = useMergeRefs([ref, refs.setFloating]);

        if (!shouldMount) {
            return null;
        }

        return (
            <DialogContext.Provider value={dialogContext}>
                <FloatingPortal>
                    {/* Root element */}
                    <FloatingOverlay
                        ref={rootRef}
                        css={[cssVariables, DIALOG_STATIC_STYLES.root]}
                        lockScroll={lockScroll}
                        data-position={position}
                        data-hidden={hidden || undefined}
                        aria-hidden={hidden || undefined}
                    >
                        {/* Backdrop element */}
                        <DialogBackdrop {...BackdropProps} />

                        {loading ? (
                            // Loading spinner
                            <Loader pos="relative" size="xl" color="common.white" />
                        ) : (
                            <FloatingFocusManager context={floatingContext} returnFocus={returnFocus}>
                                {/* Surface element */}
                                <Paper
                                    ref={handleMergedSurfaceRef}
                                    baseStyles={[DIALOG_STATIC_STYLES.surface, dynamicSurfaceStyles]}
                                    {...getFloatingProps()}
                                    aria-labelledby={labelId}
                                    aria-describedby={descriptionId}
                                    data-dialog-surface={true}
                                    data-position={position}
                                    data-transition-status={transitionStatus}
                                    data-animation-disabled={!withAnimation || undefined}
                                    shadow="l"
                                    {...SurfaceProps}
                                >
                                    {children}
                                </Paper>
                            </FloatingFocusManager>
                        )}
                    </FloatingOverlay>
                </FloatingPortal>
            </DialogContext.Provider>
        );
    }
);
